import { useRef, useState } from 'react'

import { useIntl } from 'react-intl'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  Button,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import { useUnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useManifestTableTracking } from '../../hooks/useManifestTableTracking'
import { ManifestSearchBar } from './controls/ManifestSearchBar'
import type { ActiveSort } from './FooterRowCount'
import { FooterRowCount } from './FooterRowCount'
import {
  ManifestProvider,
  getVisibleStartEnd,
  useManifestContext,
} from './ManifestContext'
import { ManifestGrid } from './ManifestGrid'
import { Pagination } from './Pagination'
import { useManifestScrollDepthTracking } from './tracking/useManifestScrollDepthTracking'
import { useManifestTracking } from './tracking/useManifestTracking'
import { FadeOutMask, Footer } from './ui'
import { INLINE_INFINITE_SCROLL_LIMIT } from './useManifestDatasource'

export const FullManifestPanel = ({
  isLoggedIn,
  listing,
  stateLoadTrigger,
}: {
  isLoggedIn: boolean
  listing: OrchestratedListingDetails
  stateLoadTrigger?: number
}) => {
  const liveIsInfiniteScrollEnabled = useFlag(
    FeatureFlags.ENABLE_HP_MANIFEST_INFINITE_SCROLL
  )
  // [BP-2376] Freeze at mount, mirroring useColumnPinning's isMobileAtMountRef
  // pattern: `limit` feeds useManifestDatasource's memoized datasource, and a
  // new datasource/cacheBlockSize reference makes AG Grid purge its
  // server-side row cache and refetch from offset 0. Reacting to a live
  // LaunchDarkly flag change mid-session would silently reset the user's
  // scroll position, so pick the mode once per mount instead.
  const isInfiniteScrollEnabledRef = useRef(liveIsInfiniteScrollEnabled)
  const isInfiniteScrollEnabled = isInfiniteScrollEnabledRef.current // oxlint-disable-line react/refs
  return (
    <ManifestProvider
      limit={isInfiniteScrollEnabled ? INLINE_INFINITE_SCROLL_LIMIT : undefined}
      listing={listing}
    >
      <FullManifestPanelContent
        isInfiniteScrollEnabled={isInfiniteScrollEnabled}
        isLoggedIn={isLoggedIn}
        listing={listing}
        stateLoadTrigger={stateLoadTrigger}
      />
    </ManifestProvider>
  )
}

const FullManifestPanelContent = ({
  isInfiniteScrollEnabled,
  isLoggedIn,
  listing,
  stateLoadTrigger,
}: {
  isInfiniteScrollEnabled: boolean
  isLoggedIn: boolean
  listing: OrchestratedListingDetails
  stateLoadTrigger?: number
}) => {
  const {
    columnDefs,
    gridApi,
    isLoading,
    limit,
    total: datasourceTotal,
    visibleRange,
  } = useManifestContext()

  const {
    onManifestFullyViewed,
    onManifestPageChanged,
    onManifestScrollDepth,
  } = useManifestTableTracking(listing)
  const { containerRef, agGridProps: agGridTrackingProps } =
    useManifestTracking(listing)

  const [activeSort, setActiveSort] = useState<ActiveSort | null>(null)
  const [page, setPage] = useState(1)

  const { openUnAuthModal } = useUnAuthWarningModal()
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()

  const total = isLoggedIn ? datasourceTotal : listing.itemsTotal
  const { end, start } = getVisibleStartEnd(visibleRange, limit, total)

  // [BP-2378] Pagination (and the onManifestPageChanged call it drives) is
  // hidden under infinite scroll, so scroll depth is tracked here instead.
  useManifestScrollDepthTracking({
    enabled: isLoggedIn && isInfiniteScrollEnabled,
    onFullyViewed: onManifestFullyViewed,
    onScrollDepth: onManifestScrollDepth,
    surface: 'inline',
    total,
    visibleRange,
  })

  const handleLoginToSeeManifest = () => {
    trackButtonClicked({
      buttonName: 'see_full_manifest',
      entityType: AnalyticsEntityType.BUYER,
      screenName: 'auction_detail',
      source: AnalyticsSource.HOME_PORTAL,
    })
    openUnAuthModal(
      formatMessage({ id: 'Listing.manifestAgGrid.seeFullManifest' })
    )
  }

  const grid = (
    <ManifestGrid
      agGridTrackingProps={agGridTrackingProps}
      isInfiniteScrollEnabled={isInfiniteScrollEnabled}
      isLoggedIn={isLoggedIn}
      listing={listing}
      setActiveSort={setActiveSort}
      setPage={setPage}
      stateLoadTrigger={stateLoadTrigger}
    />
  )

  return (
    <div ref={containerRef}>
      {isLoggedIn ? (
        <>
          <ManifestSearchBar />
          {grid}
          <Footer>
            <FooterRowCount
              columnDefs={columnDefs}
              end={end}
              sort={activeSort}
              start={start}
              total={total}
            />
            {!isInfiniteScrollEnabled && total > limit && (
              <Pagination
                isFetching={isLoading}
                page={page}
                perPage={limit}
                setPage={(p) => {
                  onManifestPageChanged(p)
                  gridApi?.paginationGoToPage(p - 1)
                }}
                total={total}
              />
            )}
          </Footer>
        </>
      ) : (
        <>
          <FadeOutMask>{grid}</FadeOutMask>
          <Footer>
            <Button
              appearance="secondary"
              onClick={handleLoginToSeeManifest}
              size="medium"
            >
              <FormattedMessage id="Listing.manifestAgGrid.signInToSeeFullManifest" />
            </Button>
          </Footer>
        </>
      )}
    </div>
  )
}
